import { formatDashboardAmount } from "../dashboardAmountUtils";

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const VIEW_TYPES = {
  DEPARTMENT: "department",
  SUBHEAD: "subhead",
};

export const UC_REPORT_TYPES = [
  { value: "self", label: "Self" },
  { value: "consolidated", label: "Consolidated" },
];

export const UTILIZATION_PERCENT_COLORS = {
  red: "#DC3545",
  yellow: "#FFC107",
  blue: "#2490EA",
  green: "#198754",
  neutral: "#6E6E6E",
};

export const UTILIZATION_PERCENT_TRACK_COLORS = {
  red: "#F0CFD2",
  yellow: "#F7EBC5",
  blue: "#DFF0FF",
  green: "#E2F6ED",
  neutral: "#EBEBEB",
};

export const getUtilizationPercentColor = (percent) => {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));

  if (value <= 20) {
    return {
      tone: "red",
      color: UTILIZATION_PERCENT_COLORS.red,
      track: UTILIZATION_PERCENT_TRACK_COLORS.red,
    };
  }

  if (value <= 50) {
    return {
      tone: "yellow",
      color: UTILIZATION_PERCENT_COLORS.yellow,
      track: UTILIZATION_PERCENT_TRACK_COLORS.yellow,
    };
  }

  if (value <= 75) {
    return {
      tone: "blue",
      color: UTILIZATION_PERCENT_COLORS.blue,
      track: UTILIZATION_PERCENT_TRACK_COLORS.blue,
    };
  }

  return {
    tone: "green",
    color: UTILIZATION_PERCENT_COLORS.green,
    track: UTILIZATION_PERCENT_TRACK_COLORS.green,
  };
};

export const formatUtilizationAmount = (value) => {
  const number = Number(value);
  if (Number.isNaN(number)) return "-";

  const divisorRaw =
    typeof window !== "undefined"
      ? window.localStorage?.getItem("globalAmountDivisor")
      : null;
  const divisor = Number(divisorRaw) || 1;
  return formatDashboardAmount(number, divisor, "indian");
};

export const getFyMonthOptions = (fy) => {
  if (!fy?.year) return [];

  const [startYear, endYear] = fy.year.split("-").map((y) => parseInt(y, 10));
  const fyMonths = [
    { value: "04", month: 4, year: startYear },
    { value: "05", month: 5, year: startYear },
    { value: "06", month: 6, year: startYear },
    { value: "07", month: 7, year: startYear },
    { value: "08", month: 8, year: startYear },
    { value: "09", month: 9, year: startYear },
    { value: "10", month: 10, year: startYear },
    { value: "11", month: 11, year: startYear },
    { value: "12", month: 12, year: startYear },
    { value: "01", month: 1, year: endYear },
    { value: "02", month: 2, year: endYear },
    { value: "03", month: 3, year: endYear },
  ];

  return fyMonths.map(({ value, month, year }) => ({
    value,
    label: `${MONTH_NAMES[month - 1]}-${year}`,
  }));
};

const getPreviousMonthOption = (monthOptions, selectedMonth) => {
  const currentIndex = monthOptions.findIndex((item) => item.value === selectedMonth);
  if (currentIndex <= 0) return null;
  return monthOptions[currentIndex - 1];
};

const getPreviousFyLabel = (selectedFy) => {
  if (!selectedFy?.year) return "-";
  const [startYear] = selectedFy.year.split("-").map((y) => parseInt(y, 10));
  return `${startYear - 1}-${startYear}`;
};

export const formatShortMonthLabel = (label) => {
  if (!label || label === "-") return "-";
  const [month, year] = label.split("-");
  if (!month || !year) return label;
  return `${month.slice(0, 3)}-${year.slice(-2)}`;
};

export const formatMonthYearCaption = (label) => {
  if (!label || label === "-") return "-";
  const [month, year] = label.split("-");
  if (!month || !year) return label;
  return `${month}, ${year}`;
};

export const formatMonthYearWithFy = (monthLabel, fyLabel) => {
  const monthCaption = formatMonthYearCaption(monthLabel);
  if (monthCaption === "-" || !fyLabel || fyLabel === "-") return monthCaption;
  return `${monthCaption} FY ${fyLabel}`;
};

const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatAsOnDate = (date = new Date()) => {
  const day = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

const toBudgetDetailNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const mapPrevBudgetDetails = (details = {}) => ({
  budget: toBudgetDetailNumber(details.budget),
  allocation_from_parent: toBudgetDetailNumber(
    details.allocation_from_parent ?? details.limit_from_parent
  ),
  reapp_received: toBudgetDetailNumber(details.reapp_received),
  surrender_received: toBudgetDetailNumber(details.surrender_received),
  reappropriate_to_other: toBudgetDetailNumber(details.reappropriate_to_other),
  allocation: toBudgetDetailNumber(details.allocation),
  surrender_done: toBudgetDetailNumber(details.surrender_done),
  disbursement: toBudgetDetailNumber(details.disbursement),
  reimbursement: toBudgetDetailNumber(details.reimbursement),
});

export const mapBudgetDetails = mapPrevBudgetDetails;

export const calculateBudgetFromDetails = (details = {}) => {
  const normalized = mapBudgetDetails(details);
  return (
    normalized.budget +
    normalized.allocation_from_parent +
    normalized.reapp_received +
    normalized.surrender_received
  ) -
    (normalized.allocation +
      normalized.reappropriate_to_other +
      normalized.surrender_done);
};

export const calculatePrevBudgetFromDetails = calculateBudgetFromDetails;

export const BUDGET_INFLOW_ITEMS = [
  { key: "budget", label: "Budget from Parent" },
  { key: "allocation_from_parent", label: "Allocation / Limit From Parent" },
  { key: "reapp_received", label: "Reappropriation Received" },
  { key: "surrender_received", label: "Surrender Received" },
];

export const BUDGET_OUTFLOW_ITEMS = [
  { key: "allocation", label: "Allocation / Limit to Child" },
  { key: "reappropriate_to_other", label: "Reappropriation Transferred" },
  { key: "surrender_done", label: "Surrender Transferred" },
];

/** @deprecated use BUDGET_INFLOW_ITEMS */
export const BUDGET_ADDITION_ITEMS = BUDGET_INFLOW_ITEMS;
/** @deprecated use BUDGET_OUTFLOW_ITEMS */
export const BUDGET_DEDUCTION_ITEMS = BUDGET_OUTFLOW_ITEMS;
export const PREV_BUDGET_ADDITION_ITEMS = BUDGET_INFLOW_ITEMS;
export const PREV_BUDGET_DEDUCTION_ITEMS = BUDGET_OUTFLOW_ITEMS;

const mapExpensePaymentDetails = (details = {}) => ({
  vendor_payments: toBudgetDetailNumber(
    details.vendor_payments ?? details.disbursement
  ),
  salary_payments: toBudgetDetailNumber(details.salary_payments),
  dbt_payments: toBudgetDetailNumber(
    details.dbt_payments ?? details.reimbursement
  ),
});

export const mapPrevExpenseDetails = (details = {}) =>
  mapExpensePaymentDetails(details);

export const calculatePrevExpenseFromDetails = (details = {}) => {
  const normalized = mapPrevExpenseDetails(details);
  return (
    normalized.vendor_payments +
    normalized.salary_payments +
    normalized.dbt_payments
  );
};

export const EXPENSE_PAYMENT_ITEMS = [
  { key: "vendor_payments", label: "Vendor Payments" },
  { key: "salary_payments", label: "Salary Payments" },
  { key: "dbt_payments", label: "DBT Payments" },
];

/** @deprecated use EXPENSE_PAYMENT_ITEMS */
export const PREV_EXPENSE_ITEMS = EXPENSE_PAYMENT_ITEMS;

export const mapSelectedFyExpenseDetails = (details = {}) => ({
  last_month_vendor_payments: toBudgetDetailNumber(
    details.last_month_vendor_payments ?? details.last_month_disbursement
  ),
  last_month_salary_payments: toBudgetDetailNumber(
    details.last_month_salary_payments
  ),
  last_month_dbt_payments: toBudgetDetailNumber(
    details.last_month_dbt_payments ?? details.last_month_reimbursement
  ),
  curr_month_vendor_payments: toBudgetDetailNumber(
    details.curr_month_vendor_payments ?? details.curr_month_disbursement
  ),
  curr_month_salary_payments: toBudgetDetailNumber(
    details.curr_month_salary_payments
  ),
  curr_month_dbt_payments: toBudgetDetailNumber(
    details.curr_month_dbt_payments ?? details.curr_month_reimbursement
  ),
});

const sumSelectedExpensePayments = (normalized, prefix) =>
  normalized[`${prefix}_vendor_payments`] +
  normalized[`${prefix}_salary_payments`] +
  normalized[`${prefix}_dbt_payments`];

export const calculateSelectedExpenseUptoFromDetails = (details = {}) => {
  const normalized = mapSelectedFyExpenseDetails(details);
  return sumSelectedExpensePayments(normalized, "last_month");
};

export const calculateSelectedExpenseForMonthFromDetails = (details = {}) => {
  const normalized = mapSelectedFyExpenseDetails(details);
  return sumSelectedExpensePayments(normalized, "curr_month");
};

export const calculateSelectedTotalExpenseFromDetails = (details = {}) =>
  calculateSelectedExpenseUptoFromDetails(details) +
  calculateSelectedExpenseForMonthFromDetails(details);

export const getSelectedTotalExpenseBreakdown = (details = {}) => {
  const normalized = mapSelectedFyExpenseDetails(details);
  const totalVendorPayments =
    normalized.last_month_vendor_payments + normalized.curr_month_vendor_payments;
  const totalSalaryPayments =
    normalized.last_month_salary_payments + normalized.curr_month_salary_payments;
  const totalDbtPayments =
    normalized.last_month_dbt_payments + normalized.curr_month_dbt_payments;

  return {
    totalVendorPayments,
    totalSalaryPayments,
    totalDbtPayments,
    totalExpense: totalVendorPayments + totalSalaryPayments + totalDbtPayments,
  };
};

const SELECTED_EXPENSE_PREFIX_BY_TYPE = {
  selectedUpto: "last_month",
  selectedForMonth: "curr_month",
};

export const getSelectedExpensePaymentDetails = (details = {}, expenseType) => {
  const prefix = SELECTED_EXPENSE_PREFIX_BY_TYPE[expenseType];
  if (!prefix) return mapPrevExpenseDetails(details);

  const normalized = mapSelectedFyExpenseDetails(details);
  return {
    vendor_payments: normalized[`${prefix}_vendor_payments`],
    salary_payments: normalized[`${prefix}_salary_payments`],
    dbt_payments: normalized[`${prefix}_dbt_payments`],
  };
};

const sumDetailField = (rows, section, detailsKey, field) =>
  rows.reduce(
    (total, row) =>
      total + Number(row?.[section]?.[detailsKey]?.[field] || 0),
    0
  );

export const aggregatePrevBudgetDetails = (rows = []) => {
  const keys = [
    "budget",
    "allocation_from_parent",
    "reapp_received",
    "surrender_received",
    "reappropriate_to_other",
    "allocation",
    "surrender_done",
  ];

  return keys.reduce((acc, key) => {
    acc[key] = sumDetailField(rows, "prevFy", "budgetDetails", key);
    return acc;
  }, {});
};

export const aggregatePrevExpenseDetails = (rows = []) => ({
  vendor_payments: sumDetailField(rows, "prevFy", "expenseDetails", "vendor_payments"),
  salary_payments: sumDetailField(rows, "prevFy", "expenseDetails", "salary_payments"),
  dbt_payments: sumDetailField(rows, "prevFy", "expenseDetails", "dbt_payments"),
});

export const aggregateSelectedBudgetDetails = (rows = []) => {
  const keys = [
    "budget",
    "allocation_from_parent",
    "reapp_received",
    "surrender_received",
    "reappropriate_to_other",
    "allocation",
    "surrender_done",
  ];

  return keys.reduce((acc, key) => {
    acc[key] = sumDetailField(rows, "selectedFy", "budgetDetails", key);
    return acc;
  }, {});
};

export const aggregateSelectedExpenseDetails = (rows = []) => ({
  last_month_vendor_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "last_month_vendor_payments"
  ),
  last_month_salary_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "last_month_salary_payments"
  ),
  last_month_dbt_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "last_month_dbt_payments"
  ),
  curr_month_vendor_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "curr_month_vendor_payments"
  ),
  curr_month_salary_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "curr_month_salary_payments"
  ),
  curr_month_dbt_payments: sumDetailField(
    rows,
    "selectedFy",
    "expenseDetails",
    "curr_month_dbt_payments"
  ),
});

const sumRows = (rows, getter) =>
  rows.reduce((total, row) => total + Number(getter(row) || 0), 0);

export const computeCardTotals = (rows = []) => {
  if (!rows.length) return null;

  const prevTotalBudget = sumRows(rows, (row) => row.prevFy?.totalBudget);
  const prevTotalExpense = sumRows(rows, (row) => row.prevFy?.totalExpense);
  const prevTotalBalance = sumRows(rows, (row) => row.prevFy?.totalBalance);

  const selectedTotalBudget = sumRows(rows, (row) => row.selectedFy?.totalBudget);
  const selectedUptoExpense = sumRows(
    rows,
    (row) => row.selectedFy?.expenseUptoPrevMonth
  );
  const selectedMonthExpense = sumRows(
    rows,
    (row) => row.selectedFy?.expenseForMonth
  );
  const selectedTotalExpense = sumRows(
    rows,
    (row) => row.selectedFy?.totalExpense
  );
  const selectedTotalBalance = sumRows(
    rows,
    (row) => row.selectedFy?.totalBalance
  );
  const selectedExpensePercent = selectedTotalBudget
    ? Math.round((selectedTotalExpense / selectedTotalBudget) * 100)
    : 0;

  const cumulativeTotalBudget = sumRows(
    rows,
    (row) => row.cumulative?.totalBudget
  );
  const cumulativeTotalExpense = sumRows(
    rows,
    (row) => row.cumulative?.totalExpense
  );
  const cumulativeTotalBalance = sumRows(
    rows,
    (row) => row.cumulative?.totalBalance
  );

  const prevBudgetDetails = aggregatePrevBudgetDetails(rows);
  const prevExpenseDetails = aggregatePrevExpenseDetails(rows);
  const selectedBudgetDetails = aggregateSelectedBudgetDetails(rows);
  const selectedExpenseDetails = aggregateSelectedExpenseDetails(rows);

  return {
    rowLabel: "Total",
    prevFy: {
      totalBudget: prevTotalBudget,
      totalExpense: prevTotalExpense,
      totalBalance: prevTotalBalance,
      budgetDetails: prevBudgetDetails,
      expenseDetails: prevExpenseDetails,
    },
    selectedFy: {
      totalBudget: selectedTotalBudget,
      expenseUptoPrevMonth: selectedUptoExpense,
      expenseForMonth: selectedMonthExpense,
      totalExpense: selectedTotalExpense,
      expensePercentOfBudget: selectedExpensePercent,
      totalBalance: selectedTotalBalance,
      budgetDetails: selectedBudgetDetails,
      expenseDetails: selectedExpenseDetails,
    },
    cumulative: {
      totalBudget: cumulativeTotalBudget,
      totalExpense: cumulativeTotalExpense,
      totalBalance: cumulativeTotalBalance,
    },
  };
};

export const computeGrandTotals = (cards = []) => {
  const cardTotalRows = cards
    .map((card) => computeCardTotals(card.rows))
    .filter(Boolean);

  if (!cardTotalRows.length) return null;

  const aggregated = computeCardTotals(cardTotalRows);
  if (!aggregated) return null;

  return {
    ...aggregated,
    rowLabel: "Grand Total",
  };
};

export const buildReportMeta = ({
  selectedFy,
  selectedMonth,
  monthOptions,
  viewType,
}) => {
  const selectedMonthOption = monthOptions.find((item) => item.value === selectedMonth);
  const previousMonthOption = getPreviousMonthOption(monthOptions, selectedMonth);
  const today = new Date();

  return {
    previousFyLabel: getPreviousFyLabel(selectedFy),
    selectedFyLabel: selectedFy?.year || "-",
    previousMonthLabel: previousMonthOption?.label || "-",
    selectedMonthLabel: selectedMonthOption?.label || "-",
    selectedMonthCaption: formatMonthYearCaption(selectedMonthOption?.label),
    previousMonthCaption: formatMonthYearCaption(previousMonthOption?.label),
    asOnDate: formatAsOnDate(today),
    rowColumnLabel:
      viewType === VIEW_TYPES.SUBHEAD ? "Department" : "Subhead",
  };
};
